import { ZAPORCA_PRO_PUBLIC_KEY_SPKI_B64, ZAPORCA_PRO_ALGO, ZAPORCA_PRO_HASH } from './pro-chaves';

export const STORAGE_KEY_ZAPORCA_PRO = 'zaporca_pro_license_v1';
export const EVENT_PRO_CHANGED = 'zaporca:pro-license-changed';

export interface ZapOrcaLicensePayload {
  /** Identificador do titular (e-mail ou telefone do comprador) */
  sub: string;
  /** Nome comercial ou pessoal do titular (opcional) */
  nome?: string;
  /** Modalidade da licença: vitalício (sem expiração) ou anual */
  plano: 'vitalicio' | 'anual' | 'mensal';
  /** Timestamp Unix (segundos) de emissão da licença */
  iat: number;
  /** Timestamp Unix (segundos) de expiração (-1 para permanente) */
  exp: number;
  /** Aplicativo alvo ou escopo */
  app?: 'zaporca' | 'brup-global' | string;
  /** Limite de propostas ativas (-1 = ilimitado) */
  limiteOrcamentos?: number;
}

export interface LicenseValidationResult {
  ok: boolean;
  payload?: ZapOrcaLicensePayload;
  erro?: string;
}

/** Conversão universal Base64 para Uint8Array no navegador */
function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}

/** Conversão Base64URL para Uint8Array */
function fromBase64Url(str: string): Uint8Array {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return fromBase64(padded);
}

/** Cache da chave pública compilada no Web Crypto API */
let cachedPublicKey: CryptoKey | null = null;

async function getPublicKey(): Promise<CryptoKey> {
  if (cachedPublicKey) return cachedPublicKey;
  const spkiBytes = fromBase64(ZAPORCA_PRO_PUBLIC_KEY_SPKI_B64);
  cachedPublicKey = await crypto.subtle.importKey(
    'spki',
    spkiBytes as unknown as BufferSource,
    ZAPORCA_PRO_ALGO,
    false,
    ['verify']
  );
  return cachedPublicKey;
}

/**
 * Valida a integridade criptográfica e vigência de um token de licença ZapOrca Pro.
 * Aceita prefixos `ZAPORCA-PRO.<payload>.<sig>` ou `BRUP-PRO.<payload>.<sig>`.
 */
export async function verificarTokenAssinado(tokenBruto: string): Promise<LicenseValidationResult> {
  const limpo = tokenBruto.trim();
  if (!limpo) {
    return { ok: false, erro: 'A chave de licença não pode estar vazia.' };
  }

  let tokenSemPrefixo = limpo;
  if (tokenSemPrefixo.toUpperCase().startsWith('ZAPORCA-PRO.')) {
    tokenSemPrefixo = tokenSemPrefixo.slice(12);
  } else if (tokenSemPrefixo.toUpperCase().startsWith('ZAPORCA-PRO-')) {
    tokenSemPrefixo = tokenSemPrefixo.slice(12);
  } else if (tokenSemPrefixo.toUpperCase().startsWith('BRUP-PRO.')) {
    tokenSemPrefixo = tokenSemPrefixo.slice(9);
  } else if (tokenSemPrefixo.toUpperCase().startsWith('BRUP-PRO-')) {
    tokenSemPrefixo = tokenSemPrefixo.slice(9);
  }

  const partes = tokenSemPrefixo.split('.');
  if (partes.length !== 2) {
    return {
      ok: false,
      erro: 'Formato de chave inválido. A chave do ZapOrca Pro possui duas partes assinadas criptograficamente.',
    };
  }

  const [payloadB64, sigB64] = partes;
  if (!payloadB64 || !sigB64) {
    return { ok: false, erro: 'Estrutura da chave de licença corrompida.' };
  }

  try {
    const payloadBytes = fromBase64Url(payloadB64);
    const sigBytes = fromBase64Url(sigB64);
    const payloadJson = new TextDecoder().decode(payloadBytes);
    const payload = JSON.parse(payloadJson) as Partial<ZapOrcaLicensePayload>;

    if (!payload.sub || typeof payload.iat !== 'number' || typeof payload.exp !== 'number') {
      return { ok: false, erro: 'Dados essenciais da licença ausentes ou corrompidos.' };
    }

    // Validação criptográfica com a chave pública assimétrica ECDSA P-256
    const pubKey = await getPublicKey();
    const assinaturaValida = await crypto.subtle.verify(
      { name: ZAPORCA_PRO_ALGO.name, hash: ZAPORCA_PRO_HASH },
      pubKey,
      sigBytes as unknown as BufferSource,
      payloadBytes as unknown as BufferSource
    );

    if (!assinaturaValida) {
      return {
        ok: false,
        erro: 'Assinatura digital inválida. Esta chave não foi emitida oficialmente pelo ZapOrca / BRUP.',
      };
    }

    // Checagem de expiração temporal
    const agoraSec = Math.floor(Date.now() / 1000);
    if (payload.exp !== -1 && agoraSec > payload.exp) {
      const dataExp = new Date(payload.exp * 1000).toLocaleDateString('pt-BR');
      return {
        ok: false,
        erro: `Esta licença expirou em ${dataExp}. Adquira uma renovação para continuar no plano ZapOrca Pro.`,
      };
    }

    return {
      ok: true,
      payload: payload as ZapOrcaLicensePayload,
    };
  } catch (e) {
    return {
      ok: false,
      erro: `Erro na leitura da licença: ${e instanceof Error ? e.message : 'formato inválido'}`,
    };
  }
}

/** Estado síncrono da licença em memória para checagens instantâneas sem await */
let activeProCache: ZapOrcaLicensePayload | null = null;
let initialized = false;

function carregarCacheInicial(): void {
  if (initialized) return;
  initialized = true;
  if (typeof localStorage === 'undefined') return;

  try {
    const raw = localStorage.getItem(STORAGE_KEY_ZAPORCA_PRO);
    if (!raw) return;

    let semPrefixo = raw.trim();
    if (semPrefixo.toUpperCase().startsWith('ZAPORCA-PRO.')) semPrefixo = semPrefixo.slice(12);
    else if (semPrefixo.toUpperCase().startsWith('ZAPORCA-PRO-')) semPrefixo = semPrefixo.slice(12);
    else if (semPrefixo.toUpperCase().startsWith('BRUP-PRO.')) semPrefixo = semPrefixo.slice(9);
    else if (semPrefixo.toUpperCase().startsWith('BRUP-PRO-')) semPrefixo = semPrefixo.slice(9);

    const [payloadB64] = semPrefixo.split('.');
    if (payloadB64) {
      const payloadBytes = fromBase64Url(payloadB64);
      const parsed = JSON.parse(new TextDecoder().decode(payloadBytes)) as ZapOrcaLicensePayload;
      const agoraSec = Math.floor(Date.now() / 1000);
      if (parsed.exp === -1 || agoraSec <= parsed.exp) {
        activeProCache = parsed;
      }
    }

    // Certificação criptográfica assíncrona em segundo plano
    void verificarTokenAssinado(raw).then((res) => {
      if (!res.ok) {
        activeProCache = null;
        try {
          localStorage.removeItem(STORAGE_KEY_ZAPORCA_PRO);
        } catch {
          /* ignore */
        }
        notificarMudanca(false);
      }
    });
  } catch {
    activeProCache = null;
  }
}

function notificarMudanca(ativo: boolean): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(EVENT_PRO_CHANGED, {
        detail: { ativo, payload: activeProCache },
      })
    );
  }
}

/** Retorna sincronamente se o ZapOrca Pro está ativo */
export function isZapOrcaProActive(): boolean {
  carregarCacheInicial();
  if (!activeProCache) return false;
  if (activeProCache.exp === -1) return true;
  const agoraSec = Math.floor(Date.now() / 1000);
  return agoraSec <= activeProCache.exp;
}

/** Retorna o payload da licença ativa ou null */
export function getZapOrcaProPayload(): ZapOrcaLicensePayload | null {
  carregarCacheInicial();
  return isZapOrcaProActive() ? activeProCache : null;
}

/**
 * Ativa uma licença validada no navegador e salva no localStorage.
 */
export async function ativarLicencaPro(token: string): Promise<LicenseValidationResult> {
  const res = await verificarTokenAssinado(token);
  if (!res.ok || !res.payload) {
    return res;
  }

  activeProCache = res.payload;
  initialized = true;

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_ZAPORCA_PRO, token.trim());
    }
  } catch {
    /* ignore */
  }

  notificarMudanca(true);
  return res;
}

/**
 * Remove a licença do dispositivo e restaura o plano Grátis.
 */
export function desativarLicencaPro(): void {
  activeProCache = null;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_ZAPORCA_PRO);
    }
  } catch {
    /* ignore */
  }
  notificarMudanca(false);
}

/** Registra listener reativo para mudança de status Pro */
export function onProStatusChange(callback: (ativo: boolean, payload: ZapOrcaLicensePayload | null) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => {
    const custom = e as CustomEvent<{ ativo: boolean; payload: ZapOrcaLicensePayload | null }>;
    callback(custom.detail?.ativo ?? false, custom.detail?.payload ?? null);
  };
  window.addEventListener(EVENT_PRO_CHANGED, handler);
  return () => window.removeEventListener(EVENT_PRO_CHANGED, handler);
}
